import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useCountryLabel } from 'hooks/useCountryLabel';
import { Card, Typography, Skeleton } from 'antd';
import { MappedReturn } from 'store/returns/models/returnData';
import { DataLogStatus } from 'store/app/enums';
import { AvatarSizes, renderAvatar } from 'utils/render-avatar';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { clearOrganisationDetailsTabStorage } from 'utils/clear-organisation-tab-storage';
import { getEntityOrganisationPath } from 'utils/get-entity-organisation-path';
import { ProviderIcon } from 'components/Custom/ProviderIcon';
import { ProviderLogoNav } from 'components/Custom/ProviderLogoNav';
import styled from 'styled-components';
import { getDataLogStatusLabel } from 'utils/display-enum-label';
import { formatDisplayInteger } from 'i18n/arabicDigits';
import { formatDisplayDate, formatDisplayDateRange } from 'i18n/calendarLabels';

const { Text } = Typography;

interface BoardViewProps {
  mappedReturns: MappedReturn[];
  vatGenerate: (
    connectionId: string,
    mappingId: string,
    dataLogId?: string
  ) => void;
  loading?: boolean;
  visibleStatuses: string[];
  statusOrder?: string[];
  isClientLocked?: boolean;
}

const BoardWrapper = styled.div`
  width: 100%;
  max-width: 100%;
  border-radius: 8px;
  box-sizing: border-box;
  height: calc(100vh - 292px);
  max-height: calc(100vh - 292px);
  overflow-x: hidden;
  overflow-y: hidden;
  display: flex;
  flex-direction: column;
`;

const BoardContainer = styled.div`
  display: flex;
  overflow-x: auto;
  overflow-y: hidden;
  gap: 12px;
  flex: 1;
  min-height: 0;
  min-width: 0;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  scrollbar-width: thin;
  padding-right: 12px;

  &:focus-visible {
    outline: 2px solid var(--color-primary, #41aaaf);
    outline-offset: 2px;
  }

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--color-border, #dbeae3);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: var(--color-primary, #41aaaf);
  }
`;

const LaneContainer = styled.div`
  min-width: 300px;
  max-width: 300px;
  background: var(--color-surface, #f3f7f8);
  border-radius: 8px 8px 0 0;
  padding: 16px 8px 0 8px;
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100%;
  border: 1px solid var(--color-border, #dbeae3);
`;

const LaneHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid var(--color-border, #dbeae3);
`;

const LaneTitle = styled(Text)`
  font-weight: 600;
  font-size: 14px;
  color: var(--color-text-base, var(--color-text-dark, #042a2a)) !important;
`;

const LaneCount = styled.span`
  background: var(--color-primary, #41aaaf);
  color: var(--color-on-primary, #ffffff);
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  margin-left: 8px;
`;

const CardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
  flex: 1;
  min-height: 100px;
  padding-right: 2px;
  scrollbar-width: thin;

  &:focus-visible {
    outline: 2px solid var(--color-primary, #41aaaf);
    outline-offset: 2px;
  }

  &::-webkit-scrollbar {
    width: 6px;
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--color-border, #dbeae3);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

export const SubmissionCard = styled(Card)`
  background: var(--color-surface-raised, #ffffff) !important;
  border-radius: 8px;
  border: 1px solid var(--color-border, #dbeae3) !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
  height: 180px;
  max-height: 180px;
  min-height: 180px;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  overflow: hidden;
  color: var(--color-text-base, var(--color-text-dark, #042a2a));

  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    border-color: var(--color-primary, #41aaaf) !important;
  }

  .ant-card-body {
    padding: 16px;
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow: hidden;
    background: transparent !important;
    color: var(--color-text-base, var(--color-text-dark, #042a2a)) !important;
  }
`;

export const EntityContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`;

export const ProviderLogo = styled.a`
  display: block;
  height: 24px;
  flex-shrink: 0;

  img,
  svg {
    height: 24px;
    width: auto;
  }
`;

export const CardField = styled.div`
  margin-bottom: 0;
  font-size: 12px;
  color: var(--color-text-base, #2e2e2e);
`;

export const DueDateField = styled.div<{ $isExpired: boolean }>`
  margin-bottom: 0;
  font-size: 12px;
  color: ${(props) =>
    props.$isExpired
      ? 'var(--color-status-danger, #d13131)'
      : 'var(--color-text-base, #2e2e2e)'};
`;

export const LastActivityField = styled.div`
  margin-bottom: 0;
  font-size: 11px;
  color: var(--color-text-muted, #5a6a6a);
  font-style: italic;
  margin-top: auto;
  padding-top: 3px;
`;

export const UsersContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  padding-top: 8px;
`;

export const UserItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const UserLabel = styled(Text)`
  font-size: 10px;
  color: var(--color-text-muted, #5a6a6a) !important;
`;

// Define status order - Not Created first, Submitted last
// Exclude EXCEPTIONS_MODIFIED and CANCELLED
const STATUS_ORDER = [
  DataLogStatus.NOT_STARTED, // Not Created (empty status)
  DataLogStatus.SAVED,
  DataLogStatus.NOT_SAVED,
  DataLogStatus.AWAITING_APPROVAL,
  DataLogStatus.INTERNAL_APPROVED,
  DataLogStatus.APPROVED,
  DataLogStatus.AWAITING_CLIENT_APPROVAL,
  DataLogStatus.CLIENT_APPROVED,
  DataLogStatus.LOCKED,
  DataLogStatus.SUBMITTED, // Last
];

const getStatusDisplayOrder = (status: string, order: string[]): number => {
  const index = order.indexOf(status);
  return index === -1 ? order.length : index;
};

export const BoardView: React.FC<BoardViewProps> = ({
  mappedReturns,
  vatGenerate,
  loading = false,
  visibleStatuses,
  statusOrder,
  isClientLocked = false,
}) => {
  const { t, i18n } = useTranslation();
  const { localizeCountryLabel, localizeReturnLabel } = useCountryLabel();
  // Filter out EXCEPTIONS_MODIFIED and CANCELLED, and apply user preferences
  // Also deduplicate items by mappingId and dataLog.uuid to prevent duplicate keys
  // If same item appears with different statuses, keep only the first occurrence
  const filteredReturns = useMemo(() => {
    const seen = new Set<string>();
    const result: MappedReturn[] = [];
    
    // visibleStatuses already contains the correct enum values (e.g., 'Not Started' for NOT_STARTED)
    // We just need to normalize the item's status when comparing
    mappedReturns.forEach((item) => {
      const status = item.status || '';
      // Normalize empty status to NOT_STARTED for comparison
      const normalizedStatus = !status || status === '' 
        ? DataLogStatus.NOT_STARTED 
        : status;
      
      if (
        status === DataLogStatus.EXCEPTIONS_MODIFIED ||
        status === DataLogStatus.CANCELLED ||
        !visibleStatuses.includes(normalizedStatus)
      ) {
        return;
      }
      // Create unique key for deduplication - include status to handle same item with different statuses
      const uniqueKey = item.dataLog?.uuid
        ? `${item.mappingId}-${item.dataLog.uuid}`
        : `${item.connectionId}-${item.mappingId}`;
      if (!seen.has(uniqueKey)) {
        seen.add(uniqueKey);
        result.push(item);
      }
    });
    
    return result;
  }, [mappedReturns, visibleStatuses]);

  // Group returns by status - include empty statuses if selected
  const groupedByStatus = useMemo(() => {
    const groups: Record<string, MappedReturn[]> = {};

    // visibleStatuses already contains the correct enum values (e.g., 'Not Started' for NOT_STARTED)
    // Don't normalize visibleStatuses - it already has the correct values from BoardStatusSelector
    // Filter out any empty/null values and remove duplicates
    const uniqueStatuses = Array.from(new Set(visibleStatuses.filter((status) => status && status !== '')));

    // Only initialize groups for statuses that are actually in visibleStatuses
    // This ensures removed statuses don't get groups created
    uniqueStatuses.forEach((status) => {
      groups[status] = [];
    });

    // Add items to their status groups - ensure each item appears only once across all statuses
    // Use a Set to track which status each item was added to, to prevent duplicates
    const itemKeys = new Set<string>();
    filteredReturns.forEach((returnItem) => {
      // Normalize empty status to NOT_STARTED for comparison with visibleStatuses
      const normalizedStatus = !returnItem.status || returnItem.status === '' 
        ? DataLogStatus.NOT_STARTED 
        : returnItem.status;
      
      // Check if this normalized status is in visibleStatuses (which contains enum values like 'Not Started')
      // Only add items if their normalized status matches a status in visibleStatuses
      // Also check that the group exists (was initialized for visible statuses) to prevent creating groups for removed statuses
      if (visibleStatuses.includes(normalizedStatus) && groups[normalizedStatus] !== undefined) {
        // Use the normalized status for grouping (this will be 'Not Started' for empty statuses)
        const status = normalizedStatus;
        const uniqueKey = returnItem.dataLog?.uuid
          ? `${returnItem.mappingId}-${returnItem.dataLog.uuid}`
          : `${returnItem.connectionId}-${returnItem.mappingId}`;
        // Only add if this item hasn't been added to any status group yet
        // This ensures each item appears only once across all statuses
        if (!itemKeys.has(uniqueKey)) {
          itemKeys.add(uniqueKey);
          groups[status].push(returnItem);
        }
      }
    });

    // Sort statuses by display order (use statusOrder if provided, otherwise fall back to STATUS_ORDER)
    // CRITICAL: Only sort statuses that are actually in visibleStatuses
    // Filter uniqueStatuses to only include statuses in visibleStatuses before sorting
    // This ensures removed statuses (like 'Not Started' when unticked) don't appear in sortedStatuses
    const visibleUniqueStatuses = uniqueStatuses.filter((status) => visibleStatuses.includes(status));
    
    const orderToUse =
      statusOrder && statusOrder.length > 0 ? statusOrder : STATUS_ORDER;
    const sortedStatuses = visibleUniqueStatuses.sort((a, b) => {
      return (
        getStatusDisplayOrder(a, orderToUse) -
        getStatusDisplayOrder(b, orderToUse)
      );
    });

    // Return status groups - only statuses that are in visibleStatuses will be included
    // sortedStatuses now only contains statuses from visibleStatuses, so no additional filtering needed
    // This ensures removed statuses (like 'Not Started' when unticked) don't show empty lanes
    return sortedStatuses.map((status) => ({
      status,
      label: getDataLogStatusLabel(status || DataLogStatus.NOT_STARTED, t),
      items: groups[status] || [],
    }));
  }, [filteredReturns, visibleStatuses, statusOrder, t]);

  const handleReturnClick = (e: React.MouseEvent, record: MappedReturn) => {
    e.preventDefault();
    e.stopPropagation();
    vatGenerate(record.connectionId, record.mappingId, record.dataLog?.uuid);
  };

  const getPeriod = (record: MappedReturn, status: string): string | null => {
    // Don't show period for Not Created status
    if (!status || status === DataLogStatus.NOT_STARTED) {
      return null;
    }
    if (record.dataLog?.fromReportingDate && record.dataLog?.toReportingDate) {
      return formatDisplayDateRange(
        record.dataLog.fromReportingDate,
        record.dataLog.toReportingDate,
        'DD/MM/YYYY',
        i18n.language
      );
    }
    return null;
  };

  const isDueDateExpired = (dueDate?: string): boolean => {
    if (!dueDate) return false;
    const due = new Date(dueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return due.valueOf() < today.valueOf();
  };

  const renderUsers = (record: MappedReturn, status: string) => {
    const users: Array<{ user: any; label: string }> = [];

    if (record.dataLog?.preparerUser) {
      users.push({
        user: record.dataLog.preparerUser,
        label: t('main.preparer'),
      });
    }

    if (record.dataLog?.reviewerUser) {
      users.push({
        user: record.dataLog.reviewerUser,
        label: t('main.reviewer'),
      });
    }

    if (record.dataLog?.clientUser && !isClientLocked) {
      users.push({
        user: record.dataLog.clientUser,
        label: t('main.client'),
      });
    }

    // Don't show N/A for Not Created status
    if (users.length === 0 && (!status || status !== '')) {
      return (
        <Text style={{ fontSize: '12px', color: 'var(--color-text-muted, #5a6a6a)' }}>
          {t('billing.not-available')}
        </Text>
      );
    }

    if (users.length === 0) {
      return null;
    }

    return (
      <UsersContainer>
        {users.map(({ user, label }, index) => (
          <UserItem key={index}>
            {renderAvatar(
              user.firstName || '',
              user.lastName || '',
              AvatarSizes.SMALL,
              user.userUuid,
              user.profilePicture
            )}
            <UserLabel>{label}</UserLabel>
          </UserItem>
        ))}
      </UsersContainer>
    );
  };

  if (loading) {
    return (
      <BoardWrapper className="board-view-root">
          <BoardContainer
            tabIndex={0}
            role="region"
            aria-label={t('submissions.view-board')}
          >
            {[1, 2, 3, 4, 5].map((lane) => (
              <LaneContainer key={lane}>
                <LaneHeader>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Skeleton.Input
                      active
                      size="small"
                      style={{ width: '120px', height: '18px' }}
                    />
                    <Skeleton.Button
                      active
                      size="small"
                      style={{
                        width: '28px',
                        height: '20px',
                        marginLeft: '8px',
                        borderRadius: '12px',
                        background: 'var(--color-primary, #41aaaf)',
                      }}
                    />
                  </div>
                </LaneHeader>
                <CardsContainer>
                  {[1, 2, 3].map((card) => (
                    <SubmissionCard key={card}>
                      <div
                        style={{
                          padding: '16px',
                          display: 'flex',
                          flexDirection: 'column',
                          height: '100%',
                        }}
                      >
                        {/* Provider icon + Organization name */}
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginBottom: '12px',
                          }}
                        >
                          <Skeleton.Avatar
                            active
                            size={24}
                            shape="square"
                            style={{ borderRadius: '4px', flexShrink: 0 }}
                          />
                          <Skeleton.Input
                            active
                            size="small"
                            style={{ flex: 1, height: '16px', width: '100%' }}
                          />
                        </div>

                        {/* Mapping name (bold title) */}
                        <Skeleton.Input
                          active
                          size="default"
                          style={{
                            width: '80%',
                            height: '18px',
                            marginBottom: '12px',
                            fontWeight: 600,
                          }}
                        />

                        {/* Period */}
                        <Skeleton.Input
                          active
                          size="small"
                          style={{ width: '140px', height: '14px', marginBottom: '8px' }}
                        />

                        {/* Due date */}
                        <Skeleton.Input
                          active
                          size="small"
                          style={{ width: '120px', height: '14px', marginBottom: '12px' }}
                        />

                        {/* Users section */}
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            flexWrap: 'wrap',
                            marginTop: 'auto',
                            paddingTop: '8px',
                          }}
                        >
                          <Skeleton.Avatar active size={24} shape="circle" />
                          <Skeleton.Input
                            active
                            size="small"
                            style={{ width: '50px', height: '12px' }}
                          />
                          <Skeleton.Avatar
                            active
                            size={24}
                            shape="circle"
                            style={{ marginLeft: '-8px' }}
                          />
                          <Skeleton.Input
                            active
                            size="small"
                            style={{ width: '50px', height: '12px' }}
                          />
                        </div>

                        {/* Last activity */}
                        <Skeleton.Input
                          active
                          size="small"
                          style={{
                            width: '110px',
                            height: '12px',
                            marginTop: 'auto',
                            paddingTop: '8px',
                            fontStyle: 'italic',
                          }}
                        />
                      </div>
                    </SubmissionCard>
                  ))}
                </CardsContainer>
              </LaneContainer>
            ))}
          </BoardContainer>
        </BoardWrapper>
    );
  }

  if (groupedByStatus.length === 0) {
    return (
      <BoardWrapper className="board-view-root">
        <div
          style={{
            textAlign: 'center',
            padding: '60px 20px',
            color: 'var(--color-text-muted, #5a6a6a)',
            fontSize: '14px',
          }}
        >
          {t('submissions.no-submissions-to-display')}
        </div>
      </BoardWrapper>
    );
  }

  return (
    <BoardWrapper className="board-view-root">
      <BoardContainer
        tabIndex={0}
        role="region"
        aria-label={t('submissions.view-board')}
      >
        {groupedByStatus.map(({ status, label, items }) => (
          <LaneContainer key={status}>
            <LaneHeader>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <LaneTitle>{label}</LaneTitle>
                <LaneCount>
                  {formatDisplayInteger(items.length, i18n.language)}
                </LaneCount>
              </div>
            </LaneHeader>
            <CardsContainer
              tabIndex={0}
              role="region"
              aria-label={label}
            >
              {items.length === 0 ? (
                <div
                  style={{
                    textAlign: 'center',
                    padding: '20px',
                    color: 'var(--color-text-muted, #5a6a6a)',
                    fontSize: '12px',
                  }}
                >
                  {t('submissions.no-items')}
                </div>
              ) : (
                items.map((record, index) => {
                  const isExpired = isDueDateExpired(record.nextDueDate);
                  // Generate a truly unique key that combines multiple identifiers
                  // This ensures uniqueness even if the same item appears multiple times
                  const baseKey = record.dataLog?.uuid
                    ? `${record.mappingId}-${record.dataLog.uuid}`
                    : `${record.connectionId}-${record.mappingId}`;
                  // Include status, index, and a timestamp-like identifier to ensure absolute uniqueness
                  const uniqueKey = `${status}-${baseKey}-${index}-${record.organisationId || 'no-org'}`;


                  return (
                    <SubmissionCard
                      key={uniqueKey}
                      onClick={(e) => handleReturnClick(e, record)}
                    >
                      <EntityContainer>
                        {record.providerName && (
                          <ProviderLogoNav
                            providerName={record.providerName}
                            shortCode={record.shortCode}
                            organisationId={record.organisationId}
                            style={{
                              display: 'block',
                              height: '24px',
                              flexShrink: 0,
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ProviderIcon
                              providerName={record.providerName}
                              alt={record.providerName}
                              style={{ height: '24px', width: 'auto' }}
                            />
                          </ProviderLogoNav>
                        )}
                        <Text
                          ellipsis={{ tooltip: (record.organisation || 'N/A').replace(/United Arab Emirates/gi, 'UAE') }}
                          style={{
                            fontSize: 13,
                            color: 'var(--color-text-base, var(--color-text-dark, #042a2a))',
                            fontWeight: 500,
                            flex: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <Link
                            to={getEntityOrganisationPath(
                              record.organisationId,
                              record.providerName
                            )}
                            onClick={(e) => {
                              e.stopPropagation();
                              clearOrganisationDetailsTabStorage();
                            }}
                            style={{
                              color: 'inherit',
                              textDecoration: 'none',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.color = 'var(--color-primary, #41aaaf)';
                              e.currentTarget.style.textDecoration =
                                'underline';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.color =
                                'var(--color-text-base, var(--color-text-dark, #042a2a))';
                              e.currentTarget.style.textDecoration = 'none';
                            }}
                          >
                            {(record.organisation || 'N/A').replace(/United Arab Emirates/gi, 'UAE')}
                          </Link>
                        </Text>
                      </EntityContainer>

                      <Text
                        ellipsis={{ tooltip: record.mappingName ? localizeReturnLabel(record.mappingName.replace(/United Arab Emirates/gi, 'UAE')) : undefined }}
                        style={{
                          fontWeight: 600,
                          fontSize: 14,
                          color: 'var(--color-text-base, var(--color-text-dark, #042a2a))',
                          display: 'block',
                          marginBottom: 12,
                        }}
                      >
                        {record.mappingName
                          ? localizeReturnLabel(
                              record.mappingName.replace(
                                /United Arab Emirates/gi,
                                'UAE'
                              )
                            )
                          : record.mappingName}
                      </Text>

                      {getPeriod(record, status) && (
                        <CardField>
                          {t('submissions.period-label')} {getPeriod(record, status)}
                        </CardField>
                      )}

                      {record.nextDueDate && (
                        <DueDateField $isExpired={isExpired}>
                          {t('submissions.due-label')}{' '}
                          {formatDisplayDate(
                            record.nextDueDate,
                            'DD/MM/YYYY',
                            i18n.language
                          )}
                        </DueDateField>
                      )}

                      {renderUsers(record, status)}

                      {record.dataLog?.lastUpdatedDate && (
                        <LastActivityField>
                          {t('submissions.last-activity-label')}{' '}
                          {formatDisplayDate(
                            record.dataLog.lastUpdatedDate,
                            'DD/MM/YYYY',
                            i18n.language
                          )}
                        </LastActivityField>
                      )}
                    </SubmissionCard>
                  );
                })
              )}
            </CardsContainer>
          </LaneContainer>
        ))}
      </BoardContainer>
    </BoardWrapper>
  );
};
